/* eslint-disable @next/next/no-img-element */
import { useEffect, useState, useContext, useRef } from "react";
import {
  MultiSelectAutoComplete,
  type Options,
} from "../../shadcn/components/custom/search/MultiSelectAutoComplete";
import { Context, getSearchResults } from "services";
import { useDebouncedCallback } from "use-debounce";
import CustomSearchDropdown from "../../shadcn/components/custom/dropdown/CustomSearchDropdown";

interface HeaderSearchBarProps {
  cities: any;
}

const bankName = process.env.NEXT_PUBLIC_BANK;

export function HeaderSearchBar({ cities }: HeaderSearchBarProps) {
  const { searchForm }: any = useContext(Context);
  const parentRef = useRef<HTMLDivElement>(null);
  const [isLoading, setLoading] = useState(false);
  const [isDisabled, setDisbled] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Options[]>([]);
  const [parentWidth, setParentWidth] = useState<number>(0);

  const filterData = (dataToFilter: any) => {
    return (dataToFilter || []).map((data: any) => {
      return {
        label: data.parent.locality_name,
        value: data.parent.locality_slug,
      };
    });
  };

  const init = async (): Promise<Options[]> => {
    const initialData: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: "*",
          query_by: "dump.location.locality_slug",
          filter_by: `status:active && dump.location.city_name:${searchForm.value.city.label} && dump.bank.bank_slug:${bankName}`,
          filter: "true",
          facet_by: "dump.location.locality_slug",
          facet_return_parent: "dump.location.locality_slug",
          max_facet_values: "10000",
          per_page: 0,
        },
      ],
    });

    const processedResults = initialData?.results?.map((result: any) => {
      return {
        group: "popular",
        data: filterData(result.facet_counts?.[0]?.counts),
      };
    });

    if (processedResults) {
      return processedResults; // Return the data based on the conditions
    }
    return [];
  };

  const handleSearch = async (searchTerm: string): Promise<Options[]> => {
    if (searchTerm === "") {
      const data = await init();
      return data;
    }

    const searchedData: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: searchTerm,
          query_by: "dump.location.locality_slug",
          filter_by: `status:active && dump.location.city_name:${searchForm.value.city.label}`,
          filter: "true",
          facet_by: "dump.location.locality_slug", //dump.location.locality_name
          facet_return_parent: "dump.location.locality_slug",
          max_facet_values: "10000",
          per_page: 0,
        },
        {
          collection: "properties",
          q: "*",
          query_by: "dump.location.locality_slug",
          filter_by: `status:active && dump.location.city_name:${searchForm.value.city.label}`,
          filter: "true",
          facet_by: "dump.location.locality_slug",
          facet_return_parent: "dump.location.locality_slug",
          max_facet_values: "10000",
          per_page: 0,
        },
      ],
    });

    const processedResults = searchedData?.results?.map(
      (result: any, index: number) => {
        if (index === 0) {
          return {
            group: "locality",
            data: filterData(result.facet_counts?.[0]?.counts),
          };
        } else if (index === 1) {
          return {
            group: "not_found",
            data: filterData(result.facet_counts?.[0]?.counts),
          };
        }
      },
    );

    if (processedResults) {
      const finalResult =
        processedResults.find(
          (result: any) =>
            result?.group === "locality" && result.data.length > 0,
        ) ||
        processedResults.find(
          (result: any) => result?.group === "not_found", //recommendations
        );

      if (finalResult) {
        return [finalResult]; // Return the data based on the conditions
      }
    }
    return [];
  };

  const handleOnSearchChange = useDebouncedCallback(async (e: string) => {
    const data = await handleSearch(e);
    setOptions(data);
  }, 300);

  useEffect(() => {
    (async () => {
      await handleOnSearchChange(inputValue);
    })();
  }, [inputValue]);

  useEffect(() => {
    (async () => {
      const data = await init();
      setOptions(data);
    })();
  }, [searchForm.value.asset_type, searchForm.value.city]);

  useEffect(() => {
    const updateWidth = () => {
      if (parentRef.current) {
        setParentWidth(parentRef.current.getBoundingClientRect().width);
      }
    };
    // Initial measurement
    updateWidth();
    // Add event listener for window resize
    window.addEventListener("resize", updateWidth);

    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, [parentRef]);

  return (
    <div
      ref={parentRef}
      className={`w-full h-11 lg:h-9 lg:mr-[16px] xl:mr-[65px] bg-white flex justify-between gap-x-2 items-center rounded-lg border`}
    >
      <div className="flex items-center">
        <div className="w-[93px] md:w-[76px]">
          {/* <Dropdown
            dataSource={() => {
              return cities;
            }}
            styles={{
              outerContainer:
                "h-full py-1 pl-3 pr-[7px] text-web-body-sm gap-x-1",
            }}
            defaultSelected={searchForm.getField("city")}
            onChange={(value: any) => {
              searchForm.setField("city", value);
              searchForm.setField("state", "");
            }}
            valueKey="value"
            placeholder="City"
          /> */}
          <CustomSearchDropdown
            onValueChange={(value: any) => {
              searchForm.setField("city", value);
              searchForm.setField("state", "");
            }}
            placeholderText="City"
            defaultSelected={searchForm.getField("city")}
            dataSource={() => {
              return cities;
            }}
            styles={{
              placeholderClass: "pr-1 overflow-hidden",
              labelClass: "text-black",
              optionItemClass: "border-b py-2",
              showAllOptionAtTop: true,
              allOptionsObject: { label: "All Cities", value: "" },
            }}
          />
        </div>
        <div className={"border-l border-brand-borderGreyTwo h-5"} />
      </div>

      <MultiSelectAutoComplete
        options={options}
        emptyMessage="No results."
        placeholder={"Enter Locality"}
        className="flex-1 w-full h-full px-3 py-2 ring-none outline-none focus:ring-0 focus:outline-none border-none focus:border-none mx-auto"
        isLoading={isLoading}
        onValueChange={(v) => searchForm.setField("locality", v)}
        onInputValueChange={(value: any) => {
          setInputValue(value);
        }}
        parentWidth={parentWidth}
        onBlur={() => setInputValue("")}
        inputValue={inputValue}
        value={searchForm.getField("locality")}
        city={searchForm.getField("city")}
        disabled={isDisabled}
      />

      <div className="mr-2">
        <svg
          className="w-5 h-5 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
    </div>
  );
}
